import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import type { Section } from '../../auth/useSession'
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  InputText,
  Modal,
  Spinner,
  Table,
  Tag,
} from '../../components'
import type { TableColumn } from '../../components'
import { getErrorCode } from '../../lib/errorCode'
import { SECTION_CATALOG, sectionCopy } from '../../lib/sections'
import { trpcMessage } from '../../lib/trpcMessages'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles, changedFields, hasFields } from '../admin/AdminPage'
import { ChangesTable } from '../admin/ChangesTable'

type PlantRole = RouterOutputs['roles']['list'][number]

/** The API's caps (`roles.trpc.ts`), so a too-long value is not a round-trip. */
const NAME_MAX_LENGTH = 60
const DESCRIPTION_MAX_LENGTH = 300

const CHANGES_LISTED = 50

const OWN_ADMIN_NOTE = 'No puedes quitar «Usuarios y roles» de tu propio rol: te quedarías sin poder volver a dártelo.'

/**
 * What a refused role write says. The API's codes are shared by different
 * guards, so the copy is chosen by the code AND by what was being done:
 * CONFLICT is a taken name when saving and a role still held when deleting;
 * PRECONDITION_FAILED is one's own `admin` or the last administrator.
 */
const roleErrorMessage = (
  error: unknown,
  action: 'save' | 'sections' | 'delete',
  removesOwnAdmin = false,
): string => {
  const code = getErrorCode(error)
  if (code === 'CONFLICT') {
    return action === 'delete'
      ? 'El rol tiene personas asignadas: dales otro rol antes de eliminarlo.'
      : 'Ya existe un rol con ese nombre.'
  }
  if (code === 'PRECONDITION_FAILED') {
    return removesOwnAdmin
      ? 'No puedes quitar admin de tu propio rol.'
      : 'Tiene que quedar al menos una persona activa con admin. Dale «Usuarios y roles» a otro rol con personas activas antes.'
  }
  return trpcMessage(error)
}

const sortedSections = (sections: Array<Section>): Array<Section> =>
  SECTION_CATALOG.filter((section) => sections.includes(section))

/**
 * The roles and the sections each one opens. A section is the unit of
 * permission: the API checks it on every procedure and route, so ticking one
 * here is handing out access from the holders' next request on. Every
 * procedure behind this screen is the `admin` section.
 */
export const RolesPage = (): ReactElement => {
  const { user: session, isReadOnly } = useSession()
  const utils = trpc.useUtils()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<PlantRole | null>(null)
  const [editingSections, setEditingSections] = useState<PlantRole | null>(null)
  const [deleting, setDeleting] = useState<PlantRole | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const rolesQuery = trpc.roles.list.useQuery()
  const changesQuery = trpc.roles.listChanges.useQuery({ limit: CHANGES_LISTED })

  const refresh = (): void => {
    void utils.roles.list.invalidate()
    void utils.roles.listChanges.invalidate()
    // The session's own sections may be the ones that changed.
    void utils.auth.me.invalidate()
  }

  const deleteRole = trpc.roles.delete.useMutation({
    onSuccess: () => {
      setDeleting(null)
      setDeleteError(null)
      refresh()
    },
    onError: (error) => {
      setDeleting(null)
      setDeleteError(roleErrorMessage(error, 'delete'))
    },
  })

  const columns: Array<TableColumn<PlantRole>> = [
    {
      key: 'name',
      header: 'Rol',
      render: (role) => (
        <span className={styles['admin-page__tags']}>
          <strong>{role.name}</strong>
          {role.id === session?.roleId && <Tag tone="info" label="Tu rol" />}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Descripción',
      render: (role) => role.description ?? <span className={styles['admin-page__muted']}>—</span>,
      hideOnMobile: true,
    },
    {
      key: 'sections',
      header: 'Vistas',
      render: (role) => (role.sections.length === 0
        ? <Tag tone="warn" label="Ninguna" />
        : (
          <span className={styles['admin-page__tags']}>
            {sortedSections(role.sections).map((section) => (
              <Tag key={section} tone="neutral" label={sectionCopy(section).label} />
            ))}
          </span>
        )),
    },
    {
      key: 'users',
      header: 'Personas',
      align: 'right',
      render: (role) => <span className={styles['admin-page__number']}>{role.userCount}</span>,
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (role) => (
        <div className={styles['admin-page__row-actions']}>
          <Button
            variant="secondary"
            disabled={isReadOnly}
            onClick={() => setEditing(role)}
            aria-label={`Editar ${role.name}`}
            label="Editar"
          />
          <Button
            variant="secondary"
            disabled={isReadOnly}
            onClick={() => setEditingSections(role)}
            aria-label={`Vistas de ${role.name}`}
            label="Vistas"
          />
          {/* Disabled and explained rather than left to fail: the API refuses
              to delete a role somebody holds (CONFLICT). */}
          <Button
            variant="danger"
            disabled={isReadOnly || role.userCount > 0}
            onClick={() => {
              setDeleteError(null)
              setDeleting(role)
            }}
            aria-label={`Eliminar ${role.name}`}
            label="Eliminar"
          />
          {role.userCount > 0 && (
            <span className={styles['admin-page__row-note']}>
              {role.userCount === 1 ? 'Lo tiene 1 persona.' : `Lo tienen ${role.userCount} personas.`}
            </span>
          )}
        </div>
      ),
    },
  ]

  let list: ReactElement
  if (rolesQuery.isPending) {
    list = <Spinner label="Cargando roles…" />
  } else if (rolesQuery.isError) {
    list = <ErrorState error={rolesQuery.error} onRetry={() => void rolesQuery.refetch()} />
  } else if (rolesQuery.data.length === 0) {
    list = <EmptyState title="No hay roles" message="Crea el primero para poder dar acceso a alguien." />
  } else {
    list = <Table columns={columns} rows={rolesQuery.data} rowKey={(role) => role.id} caption="Roles" />
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Roles y vistas"
        intro="Cada persona tiene un rol, y el rol decide qué vistas abre. Un cambio aquí vale desde la siguiente petición de quien tenga el rol."
        actions={<Button disabled={isReadOnly} onClick={() => setCreating(true)} label="Nuevo rol" />}
      />
      {isReadOnly && <ReadOnlyNote />}
      {deleteError !== null && <p role="alert" className={styles['admin-page__error']}>{deleteError}</p>}

      {list}

      <section className={styles['admin-page__section']}>
        <h2 className={styles['admin-page__section-title']}>Cambios</h2>
        {changesQuery.isPending && <Spinner label="Cargando cambios…" />}
        {changesQuery.isError && <ErrorState error={changesQuery.error} onRetry={() => void changesQuery.refetch()} />}
        {changesQuery.isSuccess && changesQuery.data.length === 0 && (
          <EmptyState title="Aún no hay cambios" message="Aquí se verá quién cambió qué rol, y cuándo." />
        )}
        {changesQuery.isSuccess && changesQuery.data.length > 0 && (
          <ChangesTable changes={changesQuery.data} caption="Cambios en los roles" />
        )}
      </section>

      {creating && (
        <RoleDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            refresh()
          }}
        />
      )}

      {editing !== null && (
        <RoleDialog
          role={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            refresh()
          }}
        />
      )}

      {editingSections !== null && (
        <SectionsDialog
          role={editingSections}
          isOwnRole={editingSections.id === session?.roleId}
          onClose={() => setEditingSections(null)}
          onSaved={() => {
            setEditingSections(null)
            refresh()
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        variant="danger"
        title="Eliminar rol"
        message={deleting === null ? '' : `Se eliminará el rol «${deleting.name}» y sus vistas.`}
        confirmLabel="Eliminar"
        isLoading={deleteRole.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            deleteRole.mutate({ roleId: deleting.id })
          }
        }}
      />
    </section>
  )
}

type SectionChecklistProps = {
  selected: Array<Section>
  onChange: (sections: Array<Section>) => void
  /** A section shown checked and not changeable — one's own `admin`. */
  locked?: Section
}

/** One checkbox per section of the catalogue, each with what it opens. */
const SectionChecklist = ({ selected, onChange, locked }: SectionChecklistProps): ReactElement => (
  <fieldset className={styles['admin-page__fieldset']}>
    <legend className={styles['admin-page__legend']}>Vistas</legend>
    <div className={styles['admin-page__checklist']}>
      {SECTION_CATALOG.map((section) => (
        <label key={section} className={styles['admin-page__check-option']}>
          <input
            type="checkbox"
            className={styles['admin-page__checkbox']}
            checked={selected.includes(section)}
            disabled={section === locked}
            onChange={(event) => onChange(event.target.checked
              ? sortedSections([...selected, section])
              : selected.filter((held) => held !== section))}
          />
          <span className={styles['admin-page__check-text']}>
            {sectionCopy(section).label}
            <span className={styles['admin-page__check-description']}>{sectionCopy(section).description}</span>
          </span>
        </label>
      ))}
    </div>
  </fieldset>
)

type RoleDialogProps = {
  /** The role being edited; absent to create one. */
  role?: PlantRole
  onClose: () => void
  onSaved: () => void
}

/**
 * Creating a role (name, description and its sections) or renaming one. The
 * sections of an existing role have their own dialog: they are a different
 * procedure, with guards of their own.
 */
const RoleDialog = ({ role, onClose, onSaved }: RoleDialogProps): ReactElement => {
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [sections, setSections] = useState<Array<Section>>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onError = (error: unknown): void => setErrorMessage(roleErrorMessage(error, 'save'))
  const create = trpc.roles.create.useMutation({ onSuccess: onSaved, onError })
  const update = trpc.roles.update.useMutation({ onSuccess: onSaved, onError })
  const isPending = create.isPending || update.isPending

  const draft = { name: name.trim(), description: description.trim() === '' ? null : description.trim() }
  const patch = role === undefined ? draft : changedFields({ name: role.name, description: role.description }, draft)
  const canSubmit = draft.name !== '' && (role === undefined || hasFields(patch))

  return (
    <Modal open title={role === undefined ? 'Nuevo rol' : `Editar ${role.name}`} onClose={onClose} maxWidth="56rem">
      <form
        className={styles['admin-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          setErrorMessage(null)
          if (role === undefined) {
            create.mutate({ ...draft, sections })
          } else {
            update.mutate({ roleId: role.id, ...patch })
          }
        }}
      >
        <InputText
          label="Nombre"
          value={name}
          maxLength={NAME_MAX_LENGTH}
          onChange={(event) => setName(event.target.value)}
          required
        />
        <InputText
          label="Descripción (opcional)"
          value={description}
          maxLength={DESCRIPTION_MAX_LENGTH}
          hint="Para quién es el rol: «Jefes de turno», «Calidad»…"
          onChange={(event) => setDescription(event.target.value)}
        />
        {role === undefined && <SectionChecklist selected={sections} onChange={setSections} />}
        {errorMessage !== null && <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p>}
        <div className={styles['admin-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={isPending} label="Cancelar" />
          <Button
            type="submit"
            disabled={isPending || !canSubmit}
            label={role === undefined ? 'Crear rol' : 'Guardar cambios'}
          />
        </div>
      </form>
    </Modal>
  )
}

type SectionsDialogProps = {
  role: PlantRole
  isOwnRole: boolean
  onClose: () => void
  onSaved: () => void
}

/**
 * The sections of one role, replaced as a whole. One's own role keeps
 * `admin` locked: the API refuses to take it away (PRECONDITION_FAILED), and
 * saying so before the round-trip is kinder than after it.
 */
const SectionsDialog = ({ role, isOwnRole, onClose, onSaved }: SectionsDialogProps): ReactElement => {
  const [sections, setSections] = useState<Array<Section>>(sortedSections(role.sections))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const removesOwnAdmin = isOwnRole && !sections.includes('admin')
  const setRoleSections = trpc.roles.setSections.useMutation({
    onSuccess: onSaved,
    onError: (error) => setErrorMessage(roleErrorMessage(error, 'sections', removesOwnAdmin)),
  })
  const unchanged = JSON.stringify(sections) === JSON.stringify(sortedSections(role.sections))

  return (
    <Modal open title={`Vistas de ${role.name}`} onClose={onClose} maxWidth="56rem">
      <form
        className={styles['admin-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          setErrorMessage(null)
          setRoleSections.mutate({ roleId: role.id, sections })
        }}
      >
        <SectionChecklist
          selected={sections}
          onChange={setSections}
          locked={isOwnRole && role.sections.includes('admin') ? 'admin' : undefined}
        />
        {isOwnRole && <p className={styles['admin-page__note']}>{OWN_ADMIN_NOTE}</p>}
        {role.userCount > 0 && (
          <p className={styles['admin-page__note']}>
            {`Afecta a ${role.userCount === 1 ? '1 persona' : `${role.userCount} personas`} desde su siguiente petición.`}
          </p>
        )}
        {errorMessage !== null && <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p>}
        <div className={styles['admin-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={setRoleSections.isPending} label="Cancelar" />
          <Button type="submit" disabled={setRoleSections.isPending || unchanged} label="Guardar vistas" />
        </div>
      </form>
    </Modal>
  )
}
